/**
 * UkCoverage — the nation-grouped index of every UK university WUA
 * covers. Feeds off `GET /api/coverage/uk` which returns:
 *
 *   { nations: { england|scotland|wales|northern-ireland: {
 *                 label, unis:[{id,name,city,degree_count,...}],
 *                 total_unis, total_degrees, hesa_covered_unis } },
 *     totals: { unis, degrees, hesa_covered_unis } }
 *
 * The point of this page is to convince a family that we haven't
 * cherry-picked the Russell-Group flashy ones — every UK undergraduate
 * university is here, grouped by nation, with the honest depth (how
 * many degrees WUA has published so far + whether the salary column
 * is a HESA-linked outcome, not an editorial guess).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowRight, Loader2, ShieldCheck, Building2, Award } from "lucide-react";

const NATION_ORDER = ["england", "scotland", "wales", "northern-ireland"];

const NATION_META = {
  england: {
    tint: "bg-rose-50 border-rose-200",
    accent: "text-rose-900",
    dot: "bg-rose-500",
  },
  scotland: {
    tint: "bg-sky-50 border-sky-200",
    accent: "text-sky-900",
    dot: "bg-sky-500",
  },
  wales: {
    tint: "bg-emerald-50 border-emerald-200",
    accent: "text-emerald-900",
    dot: "bg-emerald-500",
  },
  "northern-ireland": {
    tint: "bg-amber-50 border-amber-200",
    accent: "text-amber-900",
    dot: "bg-amber-500",
  },
};

function Stat({ label, value, testId }) {
  return (
    <div className="flex flex-col" data-testid={testId}>
      <span className="text-3xl font-serif tracking-tight text-slate-900">
        {value}
      </span>
      <span className="mt-0.5 text-[11px] uppercase tracking-wider text-slate-500">
        {label}
      </span>
    </div>
  );
}

function UniRow({ uni }) {
  return (
    <Link
      to={`/uni/${uni.id}`}
      className="group flex items-center justify-between gap-2 rounded border border-slate-200 bg-white px-2.5 py-2.5 sm:px-3 hover:border-slate-400 hover:bg-slate-50 transition-colors min-w-0 overflow-hidden"
      data-testid={`uk-coverage-uni-${uni.id}`}
    >
      <div className="min-w-0 flex-1 overflow-hidden">
        <div className="flex items-center gap-1.5 min-w-0">
          {uni.world_rank ? (
            <span className="text-[10px] font-underground-sc text-slate-500 tabular-nums shrink-0">
              #{uni.world_rank}
            </span>
          ) : null}
          <span className="truncate text-sm font-medium text-slate-900 group-hover:text-slate-700 min-w-0">
            {uni.name}
          </span>
          {uni.has_hesa_salary ? (
            <span
              className="inline-flex items-center gap-1 rounded border border-emerald-200 bg-emerald-50 px-1 py-0.5 text-[9px] font-underground-sc tracking-wider text-emerald-800 shrink-0"
              title="This university carries a HESA-linked salary from the Graduate Outcomes survey."
            >
              <ShieldCheck className="h-2.5 w-2.5" />
              HESA
            </span>
          ) : null}
        </div>
        <div className="mt-0.5 truncate text-[11px] text-slate-500">
          {uni.city}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <div className="text-right">
          <div className="text-sm font-mono tabular-nums text-slate-900 leading-tight">
            {uni.degree_count}
          </div>
          <div className="text-[9px] uppercase tracking-wider text-slate-400 leading-tight">
            {uni.degree_count === 1 ? "degree" : "degrees"}
          </div>
        </div>
        <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-slate-700 transition-colors" />
      </div>
    </Link>
  );
}

function NationBlock({ code, block }) {
  const meta = NATION_META[code];
  if (!block.total_unis) return null;
  return (
    <section
      className={`rounded-lg border p-5 sm:p-6 ${meta.tint}`}
      data-testid={`uk-coverage-nation-${code}`}
    >
      <header className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2.5">
          <span className={`inline-block h-2.5 w-2.5 rounded-full ${meta.dot}`} />
          <h2 className={`text-lg font-editorial tracking-tight ${meta.accent}`}>
            {block.label}
          </h2>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] uppercase tracking-wider text-slate-600">
          <span>
            <span className="text-slate-900 font-mono tabular-nums">
              {block.total_unis}
            </span>{" "}
            unis
          </span>
          <span>
            <span className="text-slate-900 font-mono tabular-nums">
              {block.total_degrees}
            </span>{" "}
            degrees
          </span>
          <span title="Universities with a HESA-linked salary attached">
            <span className="text-emerald-800 font-mono tabular-nums">
              {block.hesa_covered_unis}
            </span>{" "}
            HESA
          </span>
        </div>
      </header>
      <div className="grid gap-2 sm:grid-cols-2">
        {block.unis.map((u) => (
          <UniRow key={u.id} uni={u} />
        ))}
      </div>
    </section>
  );
}

export default function UkCoverage() {
  const [state, setState] = useState({ loading: true, data: null, err: null });

  useEffect(() => {
    let live = true;
    api
      .get("/coverage/uk")
      .then((r) => live && setState({ loading: false, data: r.data, err: null }))
      .catch((e) => live && setState({ loading: false, data: null, err: e?.message || "failed" }));
    return () => {
      live = false;
    };
  }, []);

  if (state.loading) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-24 flex items-center justify-center text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
        Loading UK coverage…
      </div>
    );
  }
  if (state.err || !state.data) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-24 text-slate-600">
        Couldn't load UK coverage: {state.err || "empty response"}
      </div>
    );
  }
  const { nations, totals } = state.data;

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:py-14" data-testid="uk-coverage-page">
      <header className="mb-8 sm:mb-10">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-3">
          <Link to="/methodology/data-sources" className="hover:text-slate-800">
            Methodology
          </Link>
          {" · "}
          <span className="text-slate-900">UK coverage</span>
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-editorial tracking-tight text-slate-900">
          Every UK university, grouped by nation.
        </h1>
        <p className="mt-4 max-w-3xl text-base text-slate-600">
          Not a Russell-Group cherry-pick. This is every UK undergraduate
          university WhichUniversityAdvisor currently indexes — Scotland, Wales,
          Northern Ireland included — with the honest depth of how many degrees
          we've published and which entries carry a HESA-linked salary.
        </p>

        <div className="mt-6 grid grid-cols-3 gap-6 sm:gap-10">
          <Stat
            testId="uk-coverage-total-unis"
            label="Universities indexed"
            value={totals.unis}
          />
          <Stat
            testId="uk-coverage-total-degrees"
            label="Undergrad degrees"
            value={totals.degrees.toLocaleString()}
          />
          <Stat
            testId="uk-coverage-total-hesa"
            label="With HESA-linked salary"
            value={totals.hesa_covered_unis}
          />
        </div>
      </header>

      <div className="space-y-6">
        {NATION_ORDER.map((code) =>
          nations[code] ? <NationBlock key={code} code={code} block={nations[code]} /> : null
        )}
      </div>

      <footer className="mt-10 text-[12px] text-slate-500">
        <span className="inline-flex items-center gap-1.5">
          <Award className="h-3 w-3" />
          World ranks shown are WUA's world composite. Nation is derived from
          campus city; joint-campus unis (e.g. Ulster · Coleraine + Magee)
          resolve to their headquartered nation. Universities are sorted by
          WUA depth of coverage (degree count) — the entries we've published
          the most on appear first.
        </span>
      </footer>
    </div>
  );
}
